/**
 * ProductionMovePromptService
 *
 * Purpose:
 *   Tells someone still using the pilot deployment that the mobile receiving
 *   flow has landed on the real ReceivingMgr site, and gets them onto it. The
 *   pilot exists to be outgrown; this is how it says so.
 *
 * Responsibility:
 *   The "you are not on the main site" prompt and nothing else. It holds no
 *   knowledge of what the pilot is testing — only which origin is the real one.
 *
 * Notes:
 *   - The gate is deliberately NOT a `pilot: true` flag in environment.prod.ts.
 *     A flag has to be remembered and flipped, and the cost of forgetting is
 *     production telling its own users to go to production. Comparing the live
 *     origin against {@link PRODUCTION_ORIGIN} cannot fail that way: on the real
 *     site the two match and the prompt is unreachable, no matter which branch
 *     the build came from. See {@link shouldOfferProductionMove}.
 *   - What the prompt OFFERS depends on how the app is running, which is the
 *     substance of this service — see {@link ProductionMovePromptService.init}.
 *   - This app uses Ionic via NgModule (IonicModule.forRoot), so AlertController
 *     is imported from '@ionic/angular' (not the standalone entrypoint).
 */

import { Injectable, inject } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { PwaService } from './pwa.service';
import { ToastService } from './toast.service';

/**
 * The one true home of ReceivingMgr. Hardcoded rather than read off
 * `environment.appUrl`, because on the pilot build that value IS the pilot —
 * the whole point is to name somewhere the current deployment is not.
 */
export const PRODUCTION_ORIGIN = 'https://receiving-manager.macmiller.com';

/** The address as a human types it, for display and for reading aloud. */
export const PRODUCTION_HOST_LABEL = 'receiving-manager.macmiller.com';

/**
 * Whether this page should offer to move the user to production.
 *
 * True only for a production-configuration build that is being served from
 * somewhere other than the production origin — which today means the pilot
 * Static Web App, and tomorrow means any other prod-config deployment that
 * isn't the real thing (a PR preview environment, say).
 *
 * Both halves matter:
 *  - The origin check is what makes this safe. On receiving-manager.macmiller.com
 *    it is false, so once pilot merges to prod this same code ships to
 *    production and stays silent there. Nothing has to be turned off.
 *  - The build check keeps it out of dev, local and UAT, which are legitimately
 *    not production and should not be nagging anyone toward it. Only
 *    environment.prod.ts sets `production: true`.
 *
 * Pure and origin-injected so the interesting cases are testable without
 * standing up a window.
 *
 * @param isProductionBuild - environment.production for this build.
 * @param currentOrigin - The serving origin, i.e. window.location.origin.
 * @returns True when the prompt should be offered.
 */
export function shouldOfferProductionMove(
  isProductionBuild: boolean,
  currentOrigin: string
): boolean {
  if (!isProductionBuild) {
    return false;
  }
  return normalizeOrigin(currentOrigin) !== normalizeOrigin(PRODUCTION_ORIGIN);
}

/**
 * Casefolds an origin and drops a trailing slash so two spellings of the same
 * host compare equal. `window.location.origin` is already normalized, but the
 * constant above is hand-written and a future edit may not be.
 */
function normalizeOrigin(origin: string): string {
  return origin.trim().toLowerCase().replace(/\/+$/, '');
}

@Injectable({
  providedIn: 'root',
})
export class ProductionMovePromptService {
  // #region Fields
  private readonly _alertController = inject(AlertController);
  private readonly _pwaService = inject(PwaService);
  private readonly _toastService = inject(ToastService);
  private readonly _platform = inject(Platform);

  /**
   * sessionStorage key: this session has already been offered the move.
   *
   * Session rather than local storage on purpose. Per-load would punish anyone
   * with a reason to still be on pilot, and "forever" would let one stray tap
   * silence the migration for good. Per-session lands between them: it asks
   * again next time the app is opened. It also survives login() and logout(),
   * which both call localStorage.clear().
   */
  private readonly _offeredKey = 'productionMoveOffered';

  /**
   * Deferred so the alert lands after the app settles rather than during boot,
   * an MSAL redirect, or PwaUpdateService's update alert — the same reasoning
   * (and roughly the same delay) as the install prompt in AppComponent.
   */
  private readonly _delayMs = 2000;
  // #endregion

  // #region Lifecycle / Init
  /**
   * Offers the move to production, if this deployment is one that should
   * (see {@link shouldOfferProductionMove}) and this session hasn't been asked.
   *
   * Call once at app startup. Safe to call unconditionally — it no-ops
   * everywhere it shouldn't appear, production included.
   *
   * Shown whether or not the user is signed in. Someone who has not logged in
   * yet is the best person to redirect: they have nothing invested in this tab.
   */
  public init(): void {
    if (!shouldOfferProductionMove(environment.production, window.location.origin)) {
      return;
    }
    if (sessionStorage.getItem(this._offeredKey) === 'true') {
      return;
    }

    setTimeout(() => {
      void this.presentPrompt();
    }, this._delayMs);
  }
  // #endregion

  // #region Prompt
  /**
   * Presents whichever prompt suits how the app is running.
   *
   * The two cases need different endings, and the difference is the reason this
   * service is more than a redirect:
   *
   *  - In a BROWSER TAB, navigating is the whole answer. They end up on the real
   *    site in the same tab, and if they install later they install the right one.
   *
   *  - Running as the INSTALLED pilot app, navigating is close to useless. iOS
   *    does not hand an out-of-scope navigation to Safari; it opens an in-app
   *    browser view inside this app, with its own session — so they would be
   *    signing in again inside a window that still belongs to the pilot. Worse,
   *    the home-screen icon's start_url still points here, so tomorrow they are
   *    back on pilot regardless. There is no web API that can force an external
   *    browser, and no navigation of any kind can produce a second icon.
   *    So we stop trying to move the tab and tell them how to move themselves.
   *
   * The session flag is written when the prompt is shown rather than when it is
   * answered, so a user who dismisses it by other means (a backdrop tap, or
   * switching apps to follow the instructions) is not asked again immediately.
   */
  private async presentPrompt(): Promise<void> {
    sessionStorage.setItem(this._offeredKey, 'true');

    const alert = this._pwaService.isRunningStandalone()
      ? await this.buildInstalledPrompt()
      : await this.buildBrowserPrompt();

    await alert.present();
  }

  /**
   * The installed-app prompt: we cannot move them, so tell them how to move.
   */
  private buildInstalledPrompt() {
    return this._alertController.create({
      header: 'This is the pilot app',
      message:
        `The mobile receiving flow is now on the main ReceivingMgr site. To ` +
        `switch, open ${this.browserName()} and go to ${PRODUCTION_HOST_LABEL}, ` +
        `then add it to your home screen.`,
      cssClass: 'production-move-alert',
      buttons: [
        { text: 'Close', role: 'cancel' },
        {
          text: 'Copy link',
          handler: () => {
            void this.copyProductionUrl();
            // Keep the alert up: they still need the rest of the instructions,
            // and a mis-tap here shouldn't take the address off the screen.
            return false;
          },
        },
      ],
    });
  }

  /**
   * The browser-tab prompt: offer to take them there, and do it.
   */
  private buildBrowserPrompt() {
    return this._alertController.create({
      header: 'This is the pilot app',
      message:
        'The mobile receiving flow is now available on the main ReceivingMgr ' +
        'site. Want to go there?',
      cssClass: 'production-move-alert',
      buttons: [
        // Left/secondary: staying is a legitimate choice while the pilot is up.
        { text: 'Stay here', role: 'cancel' },
        // Right/primary: the move this prompt exists to make.
        {
          text: 'Take me there',
          handler: () => {
            this.goToProduction();
          },
        },
      ],
    });
  }
  // #endregion

  // #region Actions
  /**
   * Puts the production URL on the clipboard so it doesn't have to be typed —
   * the real friction in "go to this address on your phone", especially with
   * gloves on.
   *
   * Failure is reported rather than swallowed, and the toast repeats the
   * address: a silent no-op after tapping "Copy link" would leave someone
   * pasting whatever was on the clipboard before.
   */
  private async copyProductionUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(PRODUCTION_ORIGIN);
      void this._toastService.showSuccessMessage(
        `Link copied — paste it into ${this.browserName()}.`
      );
    } catch {
      void this._toastService.showWarningMessage(
        `Couldn't copy. The address is ${PRODUCTION_HOST_LABEL}`,
        8000
      );
    }
  }

  /**
   * Sends this tab to production.
   *
   * Lands on the root rather than carrying the current path across. The two
   * deployments are not guaranteed to share a route table — that is precisely
   * what the pilot is ahead on — and dropping someone onto a 404 is a worse
   * introduction to the main site than its front door. It is also a different
   * origin, so nothing in this tab's storage travels; root is the honest place
   * to restart.
   */
  private goToProduction(): void {
    window.location.assign(PRODUCTION_ORIGIN);
  }

  /**
   * What to call the browser in the instructions. Naming the actual app ("open
   * Safari") is followable; "open your browser" invites the question of which.
   *
   * The fleet is iPhones, so iOS is the case that matters and the only one named.
   * The fallback is deliberately vague rather than a second guess: desktop
   * browsers can install a PWA too, and telling a Windows user to open Safari is
   * worse than telling them nothing.
   */
  private browserName(): string {
    return this._platform.is('ios') ? 'Safari' : 'your browser';
  }
  // #endregion
}
